'use client';

/**
 * AstorWidgetChat Component
 *
 * A mini-chat panel (320px wide, ~400px tall) that provides a conversational
 * interface with Astor. UI shell only — actual SSE streaming is connected in
 * Task 8 (useAstorStream).
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { alpha, Box, Chip, CircularProgress, IconButton, InputBase, Typography, useTheme } from '@mui/material';
import { AnimatePresence, type Easing, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';

// ============================================================================
// TYPES
// ============================================================================

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AstorWidgetChatProps {
  /** Array of chat messages to display */
  messages: ChatMessage[];
  /** Callback when the user sends a message */
  onSend: (message: string) => void;
  /** Callback when the close button is clicked */
  onClose: () => void;
  /** Whether Astor is currently streaming a response */
  isStreaming?: boolean;
  /** Suggested action chips shown at the top of the chat */
  suggestedActions?: Array<{ label: string; onClick: () => void }>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CHAT_WIDTH = 320;
const CHAT_HEIGHT = 400;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const chatPanelVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25, ease: [0.215, 0.61, 0.355, 1] as Easing },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.98,
    transition: { duration: 0.15, ease: 'easeIn' as Easing },
  },
};

const messageVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: [0.215, 0.61, 0.355, 1] as Easing },
  },
};

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

// ============================================================================
// STREAMING DOTS COMPONENT
// ============================================================================

function StreamingDots(): React.JSX.Element {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'text.secondary',
            opacity: 0.5,
            animation: `astorDotPulse 1.4s infinite ease-in-out`,
            animationDelay: `${i * 0.16}s`,
            '@keyframes astorDotPulse': {
              '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.3 },
              '40%': { transform: 'scale(1)', opacity: 0.8 },
            },
          }}
        />
      ))}
    </Box>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorWidgetChat({
  messages,
  onSend,
  onClose,
  isStreaming = false,
  suggestedActions,
}: AstorWidgetChatProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const variants = prefersReducedMotion ? reducedMotionVariants : chatPanelVariants;
  const msgVariants = prefersReducedMotion ? undefined : messageVariants;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setInputValue('');
  }, [inputValue, isStreaming, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          width: CHAT_WIDTH,
          height: CHAT_HEIGHT,
          position: 'absolute',
          bottom: 68,
          right: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            background: glass.background,
            backdropFilter: glass.backdropBlur,
            WebkitBackdropFilter: glass.backdropBlur,
            border: `1px solid ${glass.border}`,
            boxShadow: glass.shadow,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: iconSizes.base, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('astorChat.title')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label={t('a11y.closeChat')}
              sx={{
                color: 'text.secondary',
                opacity: 0.7,
                '&:hover': { opacity: 1 },
              }}
            >
              <CloseIcon sx={{ fontSize: iconSizes.md }} />
            </IconButton>
          </Box>

          {/* Suggested action chips */}
          {suggestedActions && suggestedActions.length > 0 && messages.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.06)}`,
              }}
            >
              {suggestedActions.map((action, index) => (
                <Chip
                  key={`suggestion-${index}`}
                  label={action.label}
                  size="small"
                  variant="outlined"
                  onClick={action.onClick}
                  sx={{
                    fontSize: '0.75rem',
                    height: 28,
                    borderColor: isDark
                      ? alpha(theme.palette.common.white, 0.15)
                      : alpha(theme.palette.common.black, 0.15),
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: isDark
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.08),
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  color: 'text.secondary',
                  textAlign: 'center',
                  py: 4,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {t('astor.widget.placeholder')}
                </Typography>
              </Box>
            ) : (
              messages.map((message, index) => {
                // Skip empty assistant messages while streaming — the streaming
                // indicator below already shows the loading state.
                if (!message.content && message.role === 'assistant' && isStreaming) return null;
                const isUser = message.role === 'user';
                return (
                  <Box
                    key={`msg-${index}`}
                    component={prefersReducedMotion ? 'div' : motion.div}
                    {...(!prefersReducedMotion && {
                      variants: msgVariants,
                      initial: 'hidden',
                      animate: 'visible',
                    })}
                    sx={{
                      display: 'flex',
                      flexDirection: isUser ? 'row-reverse' : 'row',
                      gap: 1,
                      maxWidth: '100%',
                    }}
                  >
                    {/* Avatar */}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: isUser
                          ? isDark
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.1)
                          : isDark
                            ? alpha(theme.palette.secondary.main, 0.2)
                            : alpha(theme.palette.secondary.main, 0.1),
                        color: isUser ? 'primary.main' : 'secondary.main',
                      }}
                    >
                      {isUser ? (
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>U</Typography>
                      ) : (
                        <AutoAwesomeIcon sx={{ fontSize: iconSizes.sm }} />
                      )}
                    </Box>

                    {/* Message bubble */}
                    <Box
                      sx={{
                        maxWidth: 'calc(100% - 40px)',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2.5,
                        borderTopLeftRadius: isUser ? 10 : 4,
                        borderTopRightRadius: isUser ? 4 : 10,
                        bgcolor: isUser
                          ? isDark
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.1)
                          : isDark
                            ? alpha(theme.palette.common.white, 0.05)
                            : alpha(theme.palette.common.black, 0.04),
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}

            {/* Streaming indicator */}
            {isStreaming && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: isDark
                      ? alpha(theme.palette.secondary.main, 0.2)
                      : alpha(theme.palette.secondary.main, 0.1),
                    color: 'secondary.main',
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: iconSizes.sm }} />
                </Box>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2.5,
                    borderTopLeftRadius: 4,
                    bgcolor: isDark ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <CircularProgress size={12} sx={{ color: 'text.secondary' }} />
                  <StreamingDots />
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input area */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            <InputBase
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('astor.widget.placeholder')}
              multiline
              maxRows={3}
              disabled={isStreaming}
              fullWidth
              sx={{
                flex: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                fontSize: '0.8125rem',
                bgcolor: isDark ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.04),
                '& .MuiInputBase-input': {
                  padding: 0,
                },
              }}
            />
            <IconButton
              size="small"
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() || isStreaming}
              aria-label={t('a11y.sendMessage')}
            >
              <SendIcon sx={{ fontSize: iconSizes.base }} />
            </IconButton>
          </Box>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}

AstorWidgetChat.displayName = 'AstorWidgetChat';

export default AstorWidgetChat;
